"use client";

import Link from "next/link";
import { I } from "@/components/kamibot-v2/discord";
import { useBotStatus } from "@/hooks/useBotStatus";
import { useCountUp } from "@/hooks/useCountUp";
import { HeroNav } from "./HeroNav";
import { HeroChat } from "./HeroChat";

type HeroProps = {
  theme: "light" | "dark";
  setTheme: (t: "light" | "dark") => void;
  user: unknown;
  onInvite: () => void;
  onLogin: () => void;
};

function scrollToDemo(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  const el = document.getElementById("demo");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function Hero({ theme, setTheme, user, onInvite, onLogin }: HeroProps) {
  const isAuthed = Boolean(user);
  // onLogin은 비-CTA 진입점에서 사용될 수 있음 — 현재 Hero에는 직접 노출되지 않지만
  // 시그니처 보존 차원에서 받아둠.
  void onLogin;

  const { status } = useBotStatus();
  const guildCount = status?.guilds ?? 0;
  const guildCountText =
    guildCount > 0 ? guildCount.toLocaleString("ko-KR") : "수많은";

  // 히어로 하단 메타는 2초마다 갱신되는 실시간 근황으로 표시.
  const fmt = (n: number | undefined) => (n ?? 0).toLocaleString("ko-KR");
  const onlineText = fmt(status?.online_users);
  const audioText = fmt(status?.audio_send_handlers);

  return (
    <>
      <HeroNav theme={theme} setTheme={setTheme} onInvite={onInvite} />
      <header className="hero">
        <div className="shell hero-grid">
          <div className="hero-copy">
            <span className="hero-eyebrow">
              <span className="dot" />
              <span>매일 {guildCountText} 디스코드 서버에서 카미봇이 함께해요</span>
            </span>
            <h1 className="hero-title">
              디스코드 서버를 <br />
              <span className="accent">조금 더 부드럽게.</span>
            </h1>
            <p className="hero-sub">
              복잡한 게 싫어서 만든 봇이에요. 슬래시 한 번이면 TTS · AI · 번역 ·
              이모지 · 환영 메시지 · 음악까지, 우리 서버가 필요한 건 거의 다.
              <span
                className="gowun"
                style={{
                  display: "block",
                  marginTop: 6,
                  color: "var(--kbv2-primary-strong)",
                }}
              >
                한번 써보고 어떤지 저도 알려주세요!
              </span>
            </p>
            <div className="hero-actions">
              {isAuthed ? (
                <Link href="/dashboard" className="btn btn-primary">
                  <I.bot />
                  대시보드로 이동
                  <I.arrow />
                </Link>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onInvite}
                >
                  <I.discord />
                  서버에 카미봇 초대하기
                  <I.arrow />
                </button>
              )}
              <a
                href="#demo"
                className="btn btn-secondary"
                onClick={scrollToDemo}
              >
                <I.play /> 1분 데모 보기
              </a>
            </div>
            <div className="hero-meta">
              <span>
                <I.globe /> {guildCountText} 서버에서 활동 중
              </span>
              <span>
                <I.users /> 지금 {onlineText}명 온라인
              </span>
              <span>
                <I.volume /> 실시간 음성 송출 {audioText}개
              </span>
            </div>
          </div>
          <div className="hero-side">
            <HeroChat />
          </div>
        </div>
      </header>
    </>
  );
}
